/**
 * light_broker.ts — ONE session-fixed pool of point lights for every local,
 * short-range light that is not a street lamp (fire realism R2, 2026-09-25):
 * fires, torches and braziers (persistent SOURCES, re-submitted each frame)
 * and impacts, fireworks and explosions (transient FLASHES that decay).
 *
 * WHY ONE POOL. Clustered lighting is off by design in Atlas, so every point
 * light in the scene is evaluated for every lit fragment. Four impact lights,
 * two fire lights and the fireworks flash used to be three separate
 * always-present pools (7 lights) that could never lend each other a slot; the
 * broker is one pool (6–8 lights by tier) arbitrated by IMPORTANCE —
 * intensity / (distance + 1)² from the viewer — with the impact pool's
 * hysteresis: an incumbent is only evicted by a candidate EVICT_MARGIN× more
 * important, or two similar fires swap a slot every frame and the scene
 * strobes. Per-category QUOTAS bound each share (fires 1/2/3 by tier, impacts
 * the Wilds pool's 4 or 2), and the fires' share is RESERVED: a flash or an
 * impact never evicts a fire's light (session 3 — a persistent source must not
 * pop dark under a fireworks show). The street lamps keep their own pool — at high+
 * they are cookie SPOTLIGHTS, a different light kind (a point↔spot swap
 * re-links every lit material), so the merge is not clean.
 *
 * THE LIGHT-SET IDENTITY LAW. Membership is FIXED for the session and every
 * light stays PERMANENTLY visible: flipping `visible` changes the collected
 * light set, invalidates every lit material's pipeline and rebuilds the whole
 * scene's TSL graphs synchronously (7–12 s live-traced 2026-07-31; firing the
 * gun hung the game 2026-09-15). Idle slots are intensity 0 and parked at
 * y = −10000. Only uniforms change. `verify:blaster` and `verify:fire` assert it.
 */
import * as THREE from 'three/webgpu';

export type LightCat = 'fire' | 'impact' | 'flash';
/** A candidate must beat the incumbent by this factor to take its slot. */
export const EVICT_MARGIN = 1.6;
/** Idle slots live down here: out of every receiver's range, membership intact. */
export const PARK_Y = -10000;

export interface FlashRequest {
  cat?: LightCat;
  x: number; y: number; z: number;
  color: number;
  /** Peak intensity, decayed quadratically to 0 over `ms`. */
  intensity: number;
  rangeM: number;
  ms: number;
  /** The range clamp (default 12 m: unshadowed lights must stay local). */
  maxRangeM?: number;
}

export interface SourceRequest {
  cat?: LightCat;
  x: number; y: number; z: number;
  color: number;
  intensity: number;
  rangeM: number;
  maxRangeM?: number;
}

interface Slot {
  light: THREE.PointLight;
  kind: 'free' | 'flash' | 'source';
  cat: LightCat | null;
  key: string | null;
  left: number;
  total: number;
  peak: number;
  rank: number;
  rank0: number;
}

type Viewer = { x: number; y: number; z: number };

export class LightBroker {
  readonly group = new THREE.Group();
  private readonly slots: Slot[] = [];
  private readonly quota = new Map<LightCat, number>();
  private readonly pending = new Map<string, SourceRequest>();
  readonly stats: Record<LightCat, { taken: number; refused: number; evicted: number }> = {
    fire: { taken: 0, refused: 0, evicted: 0 }, impact: { taken: 0, refused: 0, evicted: 0 }, flash: { taken: 0, refused: 0, evicted: 0 },
  };
  private viewer: Viewer = { x: 0, y: 0, z: 0 };

  constructor(scene: THREE.Scene, capacity: number, name = 'light-broker') {
    this.group.name = name;
    scene.add(this.group);
    for (let i = 0; i < Math.max(0, capacity); i++) {
      // decay 2 = physical inverse-square falloff; distance bounds the cost.
      const l = new THREE.PointLight(0xffffff, 0, 6, 2);
      l.castShadow = false; // an unshadowed light leaks through thin walls — the range clamp bounds it
      l.visible = true;     // NEVER toggled (the light-set identity law) — parked and dark instead
      l.position.set(0, PARK_Y, 0);
      l.name = `${name}-${i}`;
      this.group.add(l);
      this.slots.push({ light: l, kind: 'free', cat: null, key: null, left: 0, total: 0, peak: 0, rank: 0, rank0: 0 });
    }
  }

  /** Create the scene's shared broker (once per session; a second call returns the first). */
  static attach(scene: THREE.Scene, capacity: number): LightBroker {
    const ud = scene.userData as { lightBroker?: LightBroker };
    if (ud.lightBroker && ud.lightBroker.group.parent === scene) return ud.lightBroker;
    const b = new LightBroker(scene, capacity);
    ud.lightBroker = b;
    return b;
  }
  /** The scene's shared broker, if one was attached. */
  static of(scene: THREE.Scene): LightBroker | null {
    const b = (scene.userData as { lightBroker?: LightBroker }).lightBroker;
    return b && b.group.parent === scene ? b : null;
  }

  get capacity(): number { return this.slots.length; }
  /** Cap how many slots a category may hold at once (default: all). */
  setQuota(cat: LightCat, n: number): void { this.quota.set(cat, Math.max(0, Math.round(n))); }
  quotaOf(cat: LightCat): number { return this.quota.get(cat) ?? this.slots.length; }
  /** Slots a category holds now. */
  usedBy(cat: LightCat): number { let n = 0; for (const s of this.slots) if (s.kind !== 'free' && s.cat === cat) n++; return n; }
  get active(): number { let n = 0; for (const s of this.slots) if (s.kind !== 'free') n++; return n; }
  /** Every slot's visibility must be true, forever. */
  get membershipIntact(): boolean { return this.slots.every((s) => s.light.visible); }
  /** Diagnostics: who holds each slot. */
  describe(): Array<{ kind: string; cat: string | null; key: string | null; intensity: number; rank: number }> {
    return this.slots.map((s) => ({ kind: s.kind, cat: s.cat, key: s.key, intensity: +s.light.intensity.toFixed(2), rank: +s.rank.toExponential(2) }));
  }

  /**
   * Importance of a light at `dist` metres from the viewer: brightness over
   * distance², so a bright far flash and a dim near one compete honestly
   * (the +1 keeps a light at the camera from dividing by zero).
   */
  static rankOf(intensity: number, dist: number): number { return intensity / ((dist + 1) * (dist + 1)); }

  private rankAt(i: number, x: number, y: number, z: number, v: Viewer): number {
    return LightBroker.rankOf(i, Math.hypot(x - v.x, y - v.y, z - v.z));
  }

  /** The slot a candidate of `cat` at `rank` may take: a free one, else the weakest it clearly beats (null = refused). */
  private pick(cat: LightCat, rank: number, avoid: Slot | null = null): { slot: Slot; evict: boolean } | null {
    const full = this.usedBy(cat) >= this.quotaOf(cat);
    if (!full) {
      const free = this.slots.find((s) => s.kind === 'free');
      if (free) return { slot: free, evict: false };
    }
    // Over its quota a category may only displace ITS OWN weakest; otherwise anyone's weakest — except a FIRE's light,
    // which only another fire may displace (session 3, 2026-09-26): the fires' share is RESERVED (the pool is sized
    // fires + 5), and a persistent source a transient flash evicts pops dark and back. It showed once the fire's light
    // moved onto the physical curve (≈ 1.3 scene units at night, below a firework flash's fixed-unit 9,000 at 200 m by
    // intensity / distance²): with a festival show running, the WebGL2 world probe read the campfire's light as 0 on
    // some frames (flame and light correlated at r 0.25).
    let weakest: Slot | null = null;
    for (const s of this.slots) {
      if (s === avoid || s.kind === 'free') continue;
      if (full && s.cat !== cat) continue;
      if (s.cat === 'fire' && s.kind === 'source' && cat !== 'fire') continue;
      if (!weakest || s.rank < weakest.rank) weakest = s;
    }
    if (!weakest || rank < weakest.rank * EVICT_MARGIN) return null;
    return { slot: weakest, evict: true };
  }

  private park(s: Slot): void {
    s.kind = 'free'; s.cat = null; s.key = null; s.left = 0; s.total = 0; s.peak = 0; s.rank = 0; s.rank0 = 0;
    s.light.intensity = 0;
    s.light.position.set(0, PARK_Y, 0); // park it; never un-visible it
  }

  /**
   * A transient light (impact, firework break, explosion): allocated NOW, then
   * decays quadratically — a flash that dies fast at the end reads hotter than
   * a linear fade over the same span. Returns true if a slot was taken.
   */
  flash(req: FlashRequest, viewer: Viewer = this.viewer): boolean {
    const cat = req.cat ?? 'flash';
    if (this.slots.length === 0 || req.ms <= 0 || req.intensity <= 0) return false;
    const rank = this.rankAt(req.intensity, req.x, req.y, req.z, viewer);
    const p = this.pick(cat, rank);
    if (!p) { this.stats[cat].refused++; return false; }
    if (p.evict) this.stats[cat].evicted++;
    const s = p.slot;
    s.kind = 'flash'; s.cat = cat; s.key = null;
    s.light.position.set(req.x, req.y, req.z);
    s.light.color.setHex(req.color);
    s.light.distance = Math.min(req.maxRangeM ?? 12, Math.max(1, req.rangeM));
    s.light.intensity = req.intensity;
    s.left = req.ms; s.total = req.ms; s.peak = req.intensity; s.rank = rank; s.rank0 = rank;
    this.stats[cat].taken++;
    return true;
  }

  /** A persistent light (a fire) for THIS frame: re-submit every frame before `update`, or it lets go. */
  source(key: string, req: SourceRequest): void { this.pending.set(key, req); }

  /** Is `key` holding a slot right now? */
  holds(key: string): boolean { return this.slots.some((s) => s.kind === 'source' && s.key === key); }
  /** The unrounded intensity of the light `key` holds (0 when it holds none) — scene units are small by day. */
  intensityOf(key: string): number { for (const s of this.slots) if (s.kind === 'source' && s.key === key) return s.light.intensity; return 0; }

  /**
   * Age the flashes (wall time — an ambient flash must not stretch when the
   * simulation hitches) and arbitrate the sources submitted this frame.
   */
  update(dtMs: number, viewer?: Viewer): void {
    if (viewer) this.viewer = { x: viewer.x, y: viewer.y, z: viewer.z };
    const v = this.viewer;
    for (const s of this.slots) {
      if (s.kind !== 'flash') continue;
      s.left -= dtMs;
      if (s.left <= 0) { this.park(s); continue; }
      const t = s.left / s.total;
      s.light.intensity = s.peak * t * t;
      s.rank = s.rank0 * t * t; // a dying flash grows easy to displace
    }
    // Sources that stopped submitting let go; the ones still there refresh.
    for (const s of this.slots) {
      if (s.kind !== 'source') continue;
      const req = s.key ? this.pending.get(s.key) : undefined;
      if (!req) { this.park(s); continue; }
      this.applySource(s, req, v);
      this.pending.delete(s.key!);
    }
    // Newcomers, the most important first.
    const fresh = [...this.pending.entries()].map(([key, req]) => ({ key, req, rank: this.rankAt(req.intensity, req.x, req.y, req.z, v) }))
      .filter((c) => c.req.intensity > 0)
      .sort((a, b) => b.rank - a.rank);
    for (const c of fresh) {
      const cat = c.req.cat ?? 'fire';
      const p = this.pick(cat, c.rank);
      if (!p) { this.stats[cat].refused++; continue; }
      if (p.evict) this.stats[cat].evicted++;
      p.slot.kind = 'source'; p.slot.cat = cat; p.slot.key = c.key;
      this.applySource(p.slot, c.req, v);
      this.stats[cat].taken++;
    }
    this.pending.clear();
  }

  private applySource(s: Slot, req: SourceRequest, v: Viewer): void {
    s.light.position.set(req.x, req.y, req.z);
    s.light.color.setHex(req.color);
    s.light.distance = Math.min(req.maxRangeM ?? 30, Math.max(1, req.rangeM));
    s.light.intensity = Math.max(0, req.intensity);
    s.rank = this.rankAt(req.intensity, req.x, req.y, req.z, v);
  }

  /** Every slot dark and parked (a world exit) — membership untouched. */
  parkAll(): void { for (const s of this.slots) this.park(s); this.pending.clear(); }

  dispose(): void {
    // Removing the group DOES change the light set — this only runs on module
    // teardown (a world exit), where the rebuild is already happening.
    this.parkAll();
    this.group.removeFromParent();
  }
}
